import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DataListDueProcessHearingType } from "src/app/shared/models/data-list";
import { DisputeCaseBaseDto } from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { DataListService } from "src/app/shared/services/data-list.service";
import { CreateUpdateDueProcessHearingDto } from "src/app/shared/models/dispute-resolution/due-process-models";
import {
  CreateUpdateIEPFacilitationMeetingDto,
} from "src/app/shared/models/dispute-resolution/iep-facilitation-models";
import { AddEditMeetingInformationComponent } from "../add-edit-meeting-information/add-edit-meeting-information.component";
import { Subscription } from "rxjs";

// Due Process "Hearings" reuse the Add/Edit Meeting modal with the noun "Hearing" and
// surveys hidden (no surveys are sent out for hearings). The modal operates on the
// IEP-meeting-shaped DTO, so this component adapts to/from the hearing-shaped DTO at
// the modal boundary.
@Component({
  selector: "app-hearings-list",
  templateUrl: "./hearings-list.component.html",
  styleUrls: ["./hearings-list.component.scss"],
  standalone: false,
})
export class HearingsListComponent implements OnInit, OnDestroy {
  @Input() disputeCase: DisputeCaseBaseDto;
  @Input() hearings: CreateUpdateDueProcessHearingDto[] = [];
  @Output() hearingsChange = new EventEmitter<
    CreateUpdateDueProcessHearingDto[]
  >();

  displayedColumns: string[] = [
    "scheduledDate",
    "type",
    "hearingHappened",
    "actions",
  ];

  hearingTypeOptions: DataListDueProcessHearingType[] = [];

  private subscriptions = new Subscription();

  constructor(
    private dataListService: DataListService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.dataListService
        .getDueProcessHearingTypes({
          pageIndex: 0,
          pageSize: 100,
        })
        .subscribe((hearingTypeOptions) => {
          this.hearingTypeOptions = hearingTypeOptions;
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  addHearing() {
    const dialogRef = this.dialog.open(AddEditMeetingInformationComponent, {
      width: "1100px",
      data: {
        disputeCase: this.disputeCase,
        meeting: null,
        meetingTypeOptions: this.hearingTypeOptions,
        noun: "Hearing",
        showSurveys: false,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const hearing = this.mapMeetingResultToHearing(result);
        this.hearings = [...this.hearings, hearing];
        this.hearingsChange.emit(this.hearings);
      }
    });
  }

  editHearing(hearing: CreateUpdateDueProcessHearingDto) {
    const dialogRef = this.dialog.open(AddEditMeetingInformationComponent, {
      width: "1100px",
      data: {
        disputeCase: this.disputeCase,
        meeting: this.mapHearingToMeeting(hearing),
        meetingTypeOptions: this.hearingTypeOptions,
        noun: "Hearing",
        showSurveys: false,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updated = this.mapMeetingResultToHearing(result);
        if (!hearing.id) {
          // Editing a newly created hearing (no id): match by reference
          const idx = this.hearings.indexOf(hearing);
          if (idx !== -1) {
            this.hearings = this.hearings.map((h, i) =>
              i === idx ? { ...h, ...updated } : h
            );
          }
        } else {
          // Editing an existing hearing (with id): match by id
          this.hearings = this.hearings.map((h) =>
            h.id && hearing.id && h.id === hearing.id ? { ...h, ...updated } : h
          );
        }
        this.hearingsChange.emit(this.hearings);
      }
    });
  }

  // hearing-shape -> modal (meeting-shape)
  private mapHearingToMeeting(
    hearing: CreateUpdateDueProcessHearingDto
  ): CreateUpdateIEPFacilitationMeetingDto {
    return {
      id: hearing.id,
      disputeCaseId: hearing.disputeCaseId,
      dateOfScheduledMeeting: hearing.dateOfScheduledHearing as Date,
      facilitationMeetingTypeId: hearing.dueProcessHearingTypeId,
      facilitationMeetingType: hearing.dueProcessHearingType,
      timeAndDurationOfFacilitationMeeting: hearing.timeAndDurationOfHearing,
      locationOfFacilitationMeeting: hearing.locationOfHearing,
      wasMeetingHeld: hearing.wasHearingHeld,
      notes: hearing.notes,
    } as CreateUpdateIEPFacilitationMeetingDto;
  }

  // modal result (meeting-shape) -> hearing-shape
  private mapMeetingResultToHearing(
    result: CreateUpdateIEPFacilitationMeetingDto
  ): CreateUpdateDueProcessHearingDto {
    return {
      id: result.id || undefined,
      disputeCaseId: this.disputeCase?.id,
      dateOfScheduledHearing: result.dateOfScheduledMeeting,
      dueProcessHearingTypeId: result.facilitationMeetingTypeId,
      dueProcessHearingType: this.hearingTypeOptions.find(
        (h) => h.id === result.facilitationMeetingTypeId
      ),
      timeAndDurationOfHearing: result.timeAndDurationOfFacilitationMeeting,
      locationOfHearing: result.locationOfFacilitationMeeting,
      wasHearingHeld: result.wasMeetingHeld,
      notes: result.notes,
    };
  }
}
